import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import Moment from "react-moment";
import {
  deleteDataById,
  postDataWithBody,
  getData,
} from "../../../../Services/Services";
import {
  deleteTaskById,
  getMatserUserTaskList,
  getThemeURL,
} from "../../../../Services/EndPoints";
import Loader from "react-spinner-loader";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Deleteicon } from "../../../../Components/IconComponents";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import { useKeycloak } from "@react-keycloak/web";
import Expandaletable from "../../../../Components/UiComponents/Table/ExpandableTable";

/**
 * UserTaskTable Component
 * 
 * A table component that displays user tasks with functionality to:
 * - View task details
 * - Delete tasks with confirmation
 * - Add new tasks
 * - Sort tasks by creation date (newest first)
 * 
 * Uses forwardRef to expose refresh functionality to parent components
 */
const UserTaskTable = forwardRef((props, ref) => {
  const { t } = useTranslation();
  
  // Component state
  const [userTaskData, setUserTaskData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [isTextFieldDisable, setIsTextFieldDisable] = useState(true);
  const [loader, setLoader] = useState(false);
  const [userId, setUserId] = useState(null);

  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
  });

  // Hooks
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  /**
   * Navigate to create new task page
   */
  const addNewHandler = () => {
    navigate("/my-tasks/create-usertask");
  };

  /**
   * Get logged-in user's email from Keycloak token
   * @returns {string} User's email address
   */
  const getLoggedInuserName = () => {
    return keycloak.idTokenParsed.email;
  };

  /**
   * Fetch task list from API and update component state
   * Sorts tasks by creation date (newest first)
   */
  const getTaskList = async () => {
    setLoader(true);
    try {
      const masterUserTaskListResponse = await postDataWithBody(
        getMatserUserTaskList,
        { email: getLoggedInuserName() },
        {}
      );
      const masterUserTaskList = masterUserTaskListResponse || [];

      // Sort tasks by creation date (newest first)
      masterUserTaskList.sort(
        (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
      );

      setUserTaskData(masterUserTaskList);
      setRowCount(masterUserTaskList.length);
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setLoader(false);
    }
  };

  /**
   * Expose refresh functionality to parent component via ref
   */
  useImperativeHandle(ref, () => ({
    refreshTaskList: getTaskList,
  }));

  /**
   * Load task list on component mount
   */
  useEffect(() => {
    fetchUserProfile();
    getTaskList();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const fetchUserProfile = async () => {
      try {
        const response = await getData(getThemeURL);
        if (response && Array.isArray(response) && response.length > 0) {
          setUserId(response[0].id);
  
        const permissions = parseUserPermissions(response[0].web_permissions);
        setUserPermissions(permissions);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

const parseUserPermissions = (webPermissions) => {
  if (!webPermissions) {
    return { canView: false, canEdit: false, canDelete: false };
  }
  const permissions = webPermissions.split(",").map(x => x.trim().toUpperCase());
  return {
    canView: permissions.includes("V"),
    canEdit: permissions.includes("E"),
    canDelete: permissions.includes("D"),
  };
};


  /**
   * Handle view button click - enables text fields for viewing
   */
  const handleClickView = () => {
    setIsTextFieldDisable(false);
  };

  /**
   * Table column configuration
   * Defines headers, data accessors, and custom cell renderers
   */
  const columns = React.useMemo(
    () => [
      {
        Header: t("dailytaskmgmnttableheaders.name"),
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: t("dailytaskmgmnttableheaders.tasktype"),
        accessor: "task_type",
        disableSortBy: true,
        Cell: (props) => (props.value === 0 ? "Activity" : "Notification"),
      },
      {
        Header: t("dailytaskmgmnttableheaders.assigntype"),
        accessor: "assign_type",
        disableSortBy: true,
        Cell: (props) => (props.value === 0 ? "Group" : "Users"),
      },
      {
        Header: t("dailytaskmgmnttableheaders.aassigntask"),
        accessor: "assign_task",
        disableSortBy: true,
        Cell: (props) => {
          const value = props.value;
          // Handle null, undefined, or empty values
          if (value === null || value === undefined || value === "") {
            return "NA";
          }
          return value === 0 ? "Anyone" : "Everyone";
        },
      },
      {
        Header: t("dailytaskmgmnttableheaders.createdon"),
        accessor: "createdOn",
        disableSortBy: true,
        Cell: (props) => (
          <Moment format="DD/MM/YYYY, hh:mm a">{props.value}</Moment>
        ),
      },
      ...(userPermissions.canView || userPermissions.canDelete
      ? [{
        Header: t("dailytaskmgmnttableheaders.action"),
        disableSortBy: true,
        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const [selectedId, setSelectedId] = useState(null);

          const deletePopUp = (id) => {
            setSelectedId(id);
            setIsdelete(!isdelete);
          };

          const deleteTableDataById = async () => {
            try {
              const response = await deleteDataById(deleteTaskById, selectedId);
              if (response?.status === "success") {
                await getTaskList();
                toast.success(response.msg || t("delete_success_message"), {
                  position: toast.POSITION.TOP_CENTER,
                  autoClose: 2000,
                });
              } else {
                toast.error(response.msg || t("delete_failure_message"), {
                  position: toast.POSITION.TOP_CENTER,
                  autoClose: 2000,
                });
              }
            } catch (error) {
              toast.error(t("delete_error_message"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
              });
              console.error("Delete error:", error);
            } finally {
              setIsdelete(false);
              setSelectedId(null);
            }
          };

          return (
            <div style={{ alignItems: "center" }}>
              {/* View Task Button */}
              {userPermissions.canView && (
                <span
                  onClick={() => {
                    navigate("/my-tasks/create-usertask", {
                      state: {
                        data: props.row.original,
                        isTextFieldDisable: isTextFieldDisable,
                        view: true,
                      },
                    });
                    handleClickView();
                  }}
                  style={{ cursor: "pointer", marginRight: "10px" }}
                  title="View Task"
                >
                  <i className="fa fa-eye"></i>
                </span>
              )}

              {/* Delete Task Button */}
              {userPermissions.canDelete && (
                <span
                  onClick={() => deletePopUp(props.row.original.id)}
                  style={{ cursor: "pointer" }}
                  title="Delete Task"
                >
                  <Deleteicon />
                </span>
              )}

              {/* Delete Confirmation Modal */}
              {isdelete && (
                <Confirmpopup
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={() => deletePopUp(null)}
                  deleteRow={deleteTableDataById}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
        }] : []),
    ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isTextFieldDisable, navigate, userPermissions] // Dependencies for memoization
  );

  return (
    <div>
      {/* Loading indicator */}
      <Loader show={loader} type="body" message="Loading Data" />
      
      <div className="container create-user">
        <div className="datatable__container">
          {/* Expandable table component */}
          <Expandaletable
            data={userTaskData}
            addButtonLabel={t("dailytaskaddbuttonlabel")}
            addNewHandler={addNewHandler}
            columns={columns}
            numberOfRows={7}
            showExport={false}
            showFilter={false}
            showReset={false}
            onExpand={false}
            rowCount={rowCount}
            headerTableHeadStyle={{ 
              background: "#1a7a73", 
              color: "#FFFFFF" 
            }}
            tableStyle={{
              border: "1px solid #f0f0f0",
              borderRadius: "12px",
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default UserTaskTable;